// Review step 3 · AI re-check & conflicts (PLAN §6, task 20). Once a document's facts are written (step 2), step 3
// lists what needs a second look:
// - conflicts with the graph: a single-valued field given two different values (two birth dates), one exclusive
//   role held by two people at the same time (two CEOs of one company), a vote both for and against;
// - internal inconsistencies within the document: the same contradictions between two of its own claims, a role or
//   event dated before the person was born or the organization founded, an "issue 12 of 10";
// - the AI re-check: claims the source does not support (hallucinations) and facts the source states that were not
//   captured (omissions).
// Each finding is resolved by a person: keep both (→ disputed), replace the old claim, reject the new one, keep it,
// add the missed fact, or "not a problem". This module holds the pure rules; the claims service applies them.

import type { ClaimKind, ClaimShape, ReviewStatus } from './claims';
import type { JobProgress, StageDef } from './progress';

export const FINDING_KINDS = ['conflict', 'inconsistency', 'unsupported', 'omission'] as const;
export type FindingKind = (typeof FINDING_KINDS)[number];

export const FINDING_KIND_LABELS: Record<FindingKind, string> = {
  conflict: 'Conflicts with the graph',
  inconsistency: 'Inconsistent within the document',
  unsupported: 'Not supported by the source',
  omission: 'Missed by the extraction',
};

/** Where a finding comes from: the graph check, the document check, or the AI re-reading the source. */
export const FINDING_ORIGINS = ['graph', 'document', 'ai'] as const;
export type FindingOrigin = (typeof FINDING_ORIGINS)[number];

export const FINDING_ACTIONS = ['keep_both', 'supersede_old', 'reject_new', 'keep', 'add', 'dismiss'] as const;
export type FindingAction = (typeof FINDING_ACTIONS)[number];
export const isFindingAction = (a: unknown): a is FindingAction => FINDING_ACTIONS.includes(a as FindingAction);

export const FINDING_ACTION_LABELS: Record<FindingAction, string> = {
  keep_both: 'Keep both (disputed)',
  supersede_old: 'Replace the old claim',
  reject_new: 'Reject the new claim',
  keep: 'Keep it',
  add: 'Add it as unreviewed',
  dismiss: 'Not a problem',
};

export const FINDING_ACTION_HELP: Record<FindingAction, string> = {
  keep_both: 'Both claims stay and are marked disputed: profiles show them side by side until a reviewer decides.',
  supersede_old: 'The claim from this document replaces the older one, which is retired (rejected) and stays in the history.',
  reject_new: 'The claim from this document is rejected; it stays in the history but is no longer shown.',
  keep: 'You checked the source: the claim stays as it is.',
  add: 'The missed fact is written as an unreviewed claim with its quote as evidence, to check on the profile or in the Review Inbox.',
  dismiss: 'Nothing changes; the finding is closed.',
};

export const FINDING_STATUSES = ['open', 'resolved'] as const;
export type FindingStatus = (typeof FINDING_STATUSES)[number];

/** The decisions a finding offers (the first is the suggested one). */
export function findingActions(f: { kind: FindingKind; otherClaimId: string | null }): FindingAction[] {
  switch (f.kind) {
    case 'conflict':
      return ['keep_both', 'supersede_old', 'reject_new'];
    case 'inconsistency':
      return f.otherClaimId ? ['keep_both', 'reject_new', 'dismiss'] : ['reject_new', 'dismiss'];
    case 'unsupported':
      return ['reject_new', 'keep'];
    case 'omission':
      return ['add', 'dismiss'];
  }
}

/** A fact the AI says the source states but review did not capture, as the AI proposed it. */
export interface OmissionProposal {
  subjectId: string;
  subjectName: string;
  predicate: string;
  objectId: string | null;
  objectName: string | null;
  value: string | null;
  date: string | null;
  claimKind: ClaimKind;
  text: string;
}

export interface FindingDetail {
  /** Why it was flagged, in plain language (the AI's reason for unsupported claims). */
  reason?: string;
  /** Evidence in the document's normalized text (omissions: the quote the AI pointed at). */
  quote?: string;
  start?: number;
  end?: number;
  omission?: OmissionProposal;
  /** Rule that raised it (for tests and the quality dashboard). */
  rule?: string;
}

export interface FindingDraft {
  kind: FindingKind;
  origin: FindingOrigin;
  /** The claim from this document the finding is about (null for omissions). */
  claimId: string | null;
  /** The other claim: an existing one (conflict) or another claim of the document (inconsistency). */
  otherClaimId: string | null;
  /** Stable per document, so running the checks again never duplicates a finding. */
  fingerprint: string;
  message: string;
  detail: FindingDetail;
}

/** What the checks need to know about a claim (the claims service builds these). */
export interface CheckClaim {
  id: string;
  subjectId: string;
  subjectName: string;
  subjectKind: string;
  predicate: string;
  /** Field or relation label ("Date of birth", "holds a role at"). */
  predicateLabel: string;
  shape: ClaimShape;
  /** The value as shown on a profile. */
  display: string;
  /** Fuzzy-date fields: the value as an interval (ISO prefixes). */
  dateValue: Interval | null;
  objectEntityId: string | null;
  objectName: string | null;
  /** Relation value: the role written on a `role_at` claim. */
  role: string | null;
  validFrom: string | null;
  validTo: string | null;
  dateOpenEnd: boolean;
  claimKind: ClaimKind;
  status: ReviewStatus;
  /** A single-valued field (one value per entity: date of birth, legal form…). */
  single: boolean;
  /** Written from the document being reviewed. */
  fromDocument: boolean;
}

// ── Dates as intervals ────────────────────────────────────────────────────────────────────────────────────────

/** Closed interval of ISO days; `hi` = 9999-12-31 for "ongoing". */
export interface Interval {
  lo: string;
  hi: string;
}

const padLo = (v: string) => (v.length === 4 ? `${v}-01-01` : v.length === 7 ? `${v}-01` : v);
const padHi = (v: string) => (v.length === 4 ? `${v}-12-31` : v.length === 7 ? `${v}-31` : v);
const OPEN_END = '9999-12-31';

/** A claim period (or a fuzzy date) as an interval; null when it has no date. */
export function intervalOf(from: string | null, to: string | null, openEnd: boolean): Interval | null {
  if (!from) return null;
  return { lo: padLo(from), hi: to ? padHi(to) : openEnd ? OPEN_END : padHi(from) };
}

/** A fuzzy-date field value (`{ from: { value }, to, openEnd }`) as an interval. */
export function fuzzyValueInterval(v: unknown): Interval | null {
  if (!v || typeof v !== 'object') return null;
  const d = v as {
    from?: { value?: unknown; span?: { from?: string; to?: string } } | null;
    to?: { value?: unknown } | null;
    openEnd?: unknown;
  };
  const from = typeof d.from?.value === 'string' ? d.from.value : null;
  const to = typeof d.to?.value === 'string' ? d.to.value : null;
  if (d.from?.span?.from && d.from.span.to && !to) return { lo: d.from.span.from, hi: d.from.span.to };
  return intervalOf(from, to, d.openEnd === true);
}

export const overlaps = (a: Interval, b: Interval) => a.lo <= b.hi && b.lo <= a.hi;
const periodOf = (c: CheckClaim) => intervalOf(c.validFrom, c.validTo, c.dateOpenEnd);
const showInterval = (i: Interval) => {
  const lo = i.lo.replace(/-01-01$/, '').replace(/-01$/, '');
  if (i.hi === OPEN_END) return `since ${lo}`;
  const hi = i.hi.replace(/-12-31$/, '').replace(/-31$/, '');
  return lo === hi || i.lo === i.hi ? lo : `${lo} – ${hi}`;
};

// ── Rules ─────────────────────────────────────────────────────────────────────────────────────────────────────

/** Roles only one person holds at a time in one organization (written on `role_at` claims). */
export const EXCLUSIVE_ROLES = [
  'ceo',
  'chief executive',
  'chief executive officer',
  'president',
  'chair',
  'chairman',
  'chairwoman',
  'chairperson',
  'director general',
  'executive director',
  'managing director',
  'general manager',
  'secretary general',
  'editor in chief',
  'director of publication',
  'directeur de la publication',
  'président',
  'présidente',
  'directeur général',
  'directrice générale',
  'rédacteur en chef',
  'rédactrice en chef',
] as const;
const EXCLUSIVE = new Set<string>(EXCLUSIVE_ROLES);
export const normalizeRole = (r: string) =>
  r
    .toLowerCase()
    .replace(/[-‐–—_./]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
export const isExclusiveRole = (r: string | null) => !!r && EXCLUSIVE.has(normalizeRole(r));

/** Participation roles of one vote: a member can't vote two ways on the same vote. */
export const VOTE_PREDICATES = ['vote_for', 'vote_against', 'vote_abstain', 'vote_absent'] as const;
const isVote = (p: string) => (VOTE_PREDICATES as readonly string[]).includes(p);

/** Fields that date an entity's start: nothing about it can happen before. */
export const START_FIELDS: Record<string, string> = { date_of_birth: 'was born', founded: 'was founded' };
/** The date of an event. */
export const EVENT_DATE_FIELD = 'date';

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
const q = (s: string) => `“${s}”`;

/** Two values of a single-valued field that can't both be true (dates: intervals that don't overlap). */
function valuesDiffer(a: CheckClaim, b: CheckClaim): boolean {
  if (a.dateValue && b.dateValue) return !overlaps(a.dateValue, b.dateValue);
  return norm(a.display) !== norm(b.display) && !!a.display.trim() && !!b.display.trim();
}

interface Pair {
  rule: string;
  message: string;
}

/** Why two claims contradict each other, or null. `a` is from the document; `b` from the document or the graph. */
export function contradiction(a: CheckClaim, b: CheckClaim): Pair | null {
  if (a.id === b.id) return null;
  if (a.claimKind === 'assessment' || b.claimKind === 'assessment') return null;
  // One value per entity (date of birth, legal form…).
  if (
    a.shape === 'attribute' &&
    b.shape === 'attribute' &&
    a.single &&
    b.single &&
    a.subjectId === b.subjectId &&
    a.predicate === b.predicate
  ) {
    if (!valuesDiffer(a, b)) return null;
    return {
      rule: 'single_value',
      message: `Two different values for ${a.predicateLabel} of ${a.subjectName}: ${q(a.display)} and ${q(b.display)}.`,
    };
  }
  // One exclusive role, two people, at the same time.
  if (
    a.predicate === 'role_at' &&
    b.predicate === 'role_at' &&
    a.objectEntityId &&
    a.objectEntityId === b.objectEntityId &&
    a.subjectId !== b.subjectId &&
    isExclusiveRole(a.role) &&
    b.role &&
    normalizeRole(a.role ?? '') === normalizeRole(b.role)
  ) {
    const pa = periodOf(a);
    const pb = periodOf(b);
    if (!pa || !pb || !overlaps(pa, pb)) return null;
    return {
      rule: 'exclusive_role',
      message: `${a.subjectName} (${showInterval(pa)}) and ${b.subjectName} (${showInterval(pb)}) are both ${a.role} of ${a.objectName ?? 'the same organization'} at the same time.`,
    };
  }
  // Two ways on one vote.
  if (
    isVote(a.predicate) &&
    isVote(b.predicate) &&
    a.predicate !== b.predicate &&
    a.subjectId === b.subjectId &&
    a.objectEntityId === b.objectEntityId
  )
    return {
      rule: 'vote',
      message: `${a.subjectName} ${a.predicateLabel} and ${b.predicateLabel} ${a.objectName ?? 'the same vote'}.`,
    };
  return null;
}

const pairKey = (a: string, b: string) => (a < b ? `${a}:${b}` : `${b}:${a}`);

/**
 * Contradictions between the document's claims and the graph (`conflict`), and between two claims of the document
 * (`inconsistency`). Only current, non-rejected claims should be passed in.
 */
export function findContradictions(claims: readonly CheckClaim[]): FindingDraft[] {
  const doc = claims.filter(c => c.fromDocument);
  const out: FindingDraft[] = [];
  const seen = new Set<string>();
  for (const a of doc) {
    for (const b of claims) {
      if (a.id === b.id || b.status === 'rejected') continue;
      const key = pairKey(a.id, b.id);
      if (seen.has(key)) continue;
      const hit = contradiction(a, b);
      if (!hit) continue;
      seen.add(key);
      const kind: FindingKind = b.fromDocument ? 'inconsistency' : 'conflict';
      out.push({
        kind,
        origin: b.fromDocument ? 'document' : 'graph',
        claimId: a.id,
        otherClaimId: b.id,
        fingerprint: `${kind}:${hit.rule}:${key}`,
        message: hit.message,
        detail: { rule: hit.rule },
      });
    }
  }
  return out;
}

/** "issue 12 of 10", "n° 7/5": a number larger than the total it is given out of. */
const N_OF_M =
  /\b(?:no\.?|n°|nr\.?|issue|number|#|vol\.?|volume|part|episode|édition|numéro)\s*(\d{1,6})\s*(?:of|\/|out of|sur)\s*(\d{1,6})\b/gi;

export function numberOutOfTotal(text: string): { n: number; total: number; match: string } | null {
  for (const m of text.matchAll(N_OF_M)) {
    const n = Number(m[1]);
    const total = Number(m[2]);
    if (total > 0 && n > total) return { n, total, match: m[0] };
  }
  return null;
}

/**
 * Internal-consistency checks within the document (PLAN §6 step 3): something dated before its subject (or the
 * organization it is about) was born or founded, a participation in an event held before the participant existed,
 * and numbers larger than their stated total. `claims` holds the document's claims plus the dates the graph knows.
 */
export function findInconsistencies(claims: readonly CheckClaim[], texts: ReadonlyMap<string, string> = new Map()): FindingDraft[] {
  const out: FindingDraft[] = [];
  // Earliest known start (birth / founding) per entity, and event dates.
  const starts = new Map<string, { at: Interval; verb: string; claimId: string }>();
  const eventDates = new Map<string, { at: Interval; claimId: string }>();
  for (const c of claims) {
    if (c.status === 'rejected' || c.claimKind === 'assessment' || !c.dateValue) continue;
    const verb = START_FIELDS[c.predicate];
    if (verb && c.shape === 'attribute') {
      const prev = starts.get(c.subjectId);
      if (!prev || c.dateValue.lo < prev.at.lo) starts.set(c.subjectId, { at: c.dateValue, verb, claimId: c.id });
    }
    if (c.predicate === EVENT_DATE_FIELD && c.subjectKind === 'event' && c.shape === 'attribute')
      eventDates.set(c.subjectId, { at: c.dateValue, claimId: c.id });
  }
  for (const a of claims.filter(c => c.fromDocument && c.status !== 'rejected')) {
    if (a.claimKind === 'assessment') continue;
    // Dated before its subject (or its object) existed.
    // The claim's own period; birth / founding and event dates are the reference points, not checked themselves.
    const when = a.shape === 'attribute' && (START_FIELDS[a.predicate] || a.predicate === EVENT_DATE_FIELD) ? null : periodOf(a);
    const ends: { id: string; name: string | null; role: 'subject' | 'object' }[] = [
      { id: a.subjectId, name: a.subjectName, role: 'subject' },
      ...(a.objectEntityId ? [{ id: a.objectEntityId, name: a.objectName, role: 'object' as const }] : []),
    ];
    const eventAt = a.objectEntityId ? eventDates.get(a.objectEntityId) : undefined;
    const dated = when ?? (eventAt && a.shape === 'relation' ? eventAt.at : null);
    if (dated) {
      for (const e of ends) {
        const s = starts.get(e.id);
        if (!s || s.claimId === a.id || dated.hi >= s.at.lo) continue;
        const what = `${a.subjectName} · ${a.predicateLabel}${a.objectName ? ` · ${a.objectName}` : ''}`;
        out.push({
          kind: 'inconsistency',
          origin: 'document',
          claimId: a.id,
          otherClaimId: s.claimId,
          fingerprint: `inconsistency:before_start:${a.id}:${e.id}`,
          message: `${what} is dated ${showInterval(dated)}, before ${e.name ?? 'it'} ${s.verb} (${showInterval(s.at)}).`,
          detail: { rule: 'before_start' },
        });
        break;
      }
    }
    // "issue 12 of 10".
    const text = [a.display, texts.get(a.id) ?? ''].join(' ');
    const nm = numberOutOfTotal(text);
    if (nm)
      out.push({
        kind: 'inconsistency',
        origin: 'document',
        claimId: a.id,
        otherClaimId: null,
        fingerprint: `inconsistency:n_of_m:${a.id}`,
        message: `${q(nm.match)}: number ${nm.n} is higher than the stated total of ${nm.total}.`,
        detail: { rule: 'n_of_m' },
      });
  }
  // A claim can hit the same start twice (subject and object): keep one per claim and start.
  const seen = new Set<string>();
  return out.filter(f => {
    if (seen.has(f.fingerprint)) return false;
    seen.add(f.fingerprint);
    return true;
  });
}

// ── The AI re-check job ───────────────────────────────────────────────────────────────────────────────────────

/** Queue message: re-read a reviewed document against what review wrote (runs in the pipeline). */
export interface ReviewRecheckMessage {
  kind: 'review_recheck';
  documentId: string;
  /** Who asked (budget, progress card). */
  userId: string;
}
export const isReviewRecheckMessage = (m: unknown): m is ReviewRecheckMessage =>
  !!m &&
  typeof m === 'object' &&
  (m as ReviewRecheckMessage).kind === 'review_recheck' &&
  typeof (m as ReviewRecheckMessage).documentId === 'string' &&
  typeof (m as ReviewRecheckMessage).userId === 'string';

/** State of the AI re-check of one document (kept on its step 3 review task). */
export const RECHECK_STATES = ['queued', 'running', 'done', 'failed', 'unavailable'] as const;
export type RecheckState = (typeof RECHECK_STATES)[number];
export interface RecheckStatus {
  state: RecheckState;
  /** Plain-language note: why it is unavailable or failed, or what it found. */
  note: string | null;
  at: number;
  route?: string | null;
  model?: string | null;
  unsupported?: number;
  omissions?: number;
}

/** A queued or running re-check with no news for this long is treated as failed (it can be run again). */
export const RECHECK_STALE_MS = 15 * 60_000;

export const RECHECK_STAGES: StageDef[] = [
  { key: 'reading', label: 'Re-reading the source', stallAfterMs: 180_000 },
  { key: 'saving', label: 'Saving findings', stallAfterMs: 60_000 },
  { key: 'done', label: 'Done', stallAfterMs: Number.POSITIVE_INFINITY },
];

export const recheckJobId = (documentId: string) => `recheck-${documentId}`;

/** The progress card of a re-check; the title is the file name, never content. */
export function recheckJob(
  run: { documentId: string; startedAt: number },
  filename: string,
  stageIndex: number,
  patch: Partial<JobProgress> = {},
): JobProgress {
  return {
    id: recheckJobId(run.documentId),
    kind: 'recheck',
    title: `AI re-check: ${filename}`,
    stages: RECHECK_STAGES,
    stageIndex,
    percent: Math.min(99, Math.round((stageIndex / (RECHECK_STAGES.length - 1)) * 100)),
    status: 'running',
    startedAt: run.startedAt,
    updatedAt: Date.now(),
    link: `/review/${run.documentId}`,
    ...patch,
  };
}
